// Shared helpers for the support / client-ticket pages.

export const LOCAL_MODE = false

export function apiUrl(path: string): string {
    return LOCAL_MODE ? `http://localhost:8000${path}` : path
}

export function credentials(): RequestCredentials {
    return LOCAL_MODE ? 'include' : 'same-origin'
}

export async function fetchCsrfToken(): Promise<string | null> {
    try {
        const res = await fetch(apiUrl('/api/csrf-token/'), { credentials: credentials() })
        if (!res.ok) return null
        return (await res.json()).csrfToken ?? null
    } catch {
        return null
    }
}

export interface ApiResult<T = any> {
    ok: boolean
    status: number
    data: T
}

/** fetch wrapper: adds credentials + CSRF, sends JSON or FormData, parses JSON. */
export async function apiRequest<T = any>(
    path: string,
    { method = 'GET', json, form }: { method?: string; json?: unknown; form?: FormData } = {},
): Promise<ApiResult<T>> {
    const headers: Record<string, string> = {}
    if (method !== 'GET') {
        const csrf = await fetchCsrfToken()
        if (csrf) headers['X-CSRFToken'] = csrf
    }

    let body: BodyInit | undefined
    if (json !== undefined) {
        headers['Content-Type'] = 'application/json'
        body = JSON.stringify(json)
    } else if (form) {
        body = form
    }

    try {
        const res = await fetch(apiUrl(path), { method, credentials: credentials(), headers, body })
        let data: any = null
        try { data = await res.json() } catch { /* empty body */ }
        return { ok: res.ok, status: res.status, data }
    } catch {
        return { ok: false, status: 0, data: { detail: 'Could not reach the server. Check your connection and try again.' } as unknown as T }
    }
}

/** Turns a DRF error payload into one readable sentence. */
export function errorMessage(data: any, fallback: string): string {
    if (!data) return fallback
    if (typeof data.detail === 'string') return data.detail
    const first = Object.values(data)[0]
    if (Array.isArray(first) && first.length) return String(first[0])
    if (typeof first === 'string') return first
    return fallback
}

export function formatDate(iso: string | null | undefined): string {
    if (!iso) return ''
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export function formatDateTime(iso: string | null | undefined): string {
    if (!iso) return ''
    return new Date(iso).toLocaleString('en-US', {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    })
}

export function formatTime(iso: string): string {
    return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

export function timeAgo(iso: string): string {
    const diff = Date.now() - new Date(iso).getTime()
    const mins = Math.floor(diff / 60000)
    const hours = Math.floor(mins / 60)
    const days = Math.floor(hours / 24)
    if (days > 0) return `${days}d ago`
    if (hours > 0) return `${hours}h ago`
    if (mins > 0) return `${mins}m ago`
    return 'just now'
}

/** 9000 → "2h 30m", 200000 → "2d 7h" */
export function formatDuration(totalSeconds: number): string {
    const s = Math.abs(Math.round(totalSeconds))
    const d = Math.floor(s / 86400)
    const h = Math.floor((s % 86400) / 3600)
    const m = Math.floor((s % 3600) / 60)
    if (d > 0) return `${d}d ${h}h`
    if (h > 0) return `${h}h ${m}m`
    return `${m}m`
}